import { useCallback, useRef, useState } from 'react';

export interface HistoryState<T> {
  past: T[];
  future: T[];
  limit: number;
}

export interface EditorHistoryDomain<T> {
  canUndo: boolean;
  canRedo: boolean;
  record: (snapshot: T) => void;
  undo: (current: T) => T | null;
  redo: (current: T) => T | null;
  clear: () => void;
}

export const createHistoryState = <T,>(limit = 40): HistoryState<T> => ({
  past: [],
  future: [],
  limit: Math.max(1, Math.trunc(limit) || 1)
});

export const recordHistory = <T,>(state: HistoryState<T>, snapshot: T): HistoryState<T> => ({
  ...state,
  past: [...state.past, snapshot].slice(-state.limit),
  future: []
});

export const undoHistory = <T,>(
  state: HistoryState<T>,
  current: T
): { state: HistoryState<T>; snapshot: T | null } => {
  const snapshot = state.past[state.past.length - 1];
  if (snapshot === undefined) return { state, snapshot: null };
  return {
    snapshot,
    state: {
      ...state,
      past: state.past.slice(0, -1),
      future: [current, ...state.future].slice(0, state.limit)
    }
  };
};

export const redoHistory = <T,>(
  state: HistoryState<T>,
  current: T
): { state: HistoryState<T>; snapshot: T | null } => {
  const snapshot = state.future[0];
  if (snapshot === undefined) return { state, snapshot: null };
  return {
    snapshot,
    state: {
      ...state,
      past: [...state.past, current].slice(-state.limit),
      future: state.future.slice(1)
    }
  };
};

export const useEditorHistoryDomain = <T,>(
  scopeKey: string | undefined,
  limit = 40
): EditorHistoryDomain<T> => {
  const storeRef = useRef({ scopeKey, history: createHistoryState<T>(limit) });
  const [, render] = useState(0);

  const resolveStore = useCallback(() => {
    if (storeRef.current.scopeKey !== scopeKey) {
      storeRef.current = { scopeKey, history: createHistoryState<T>(limit) };
    }
    return storeRef.current;
  }, [limit, scopeKey]);

  const record = useCallback((snapshot: T) => {
    const store = resolveStore();
    store.history = recordHistory(store.history, snapshot);
    render(value => value + 1);
  }, [resolveStore]);

  const undo = useCallback((current: T): T | null => {
    const store = resolveStore();
    const result = undoHistory(store.history, current);
    store.history = result.state;
    if (result.snapshot !== null) render(value => value + 1);
    return result.snapshot;
  }, [resolveStore]);

  const redo = useCallback((current: T): T | null => {
    const store = resolveStore();
    const result = redoHistory(store.history, current);
    store.history = result.state;
    if (result.snapshot !== null) render(value => value + 1);
    return result.snapshot;
  }, [resolveStore]);

  const clear = useCallback(() => {
    storeRef.current = { scopeKey, history: createHistoryState<T>(limit) };
    render(value => value + 1);
  }, [limit, scopeKey]);

  const store = resolveStore();
  return {
    canUndo: store.history.past.length > 0,
    canRedo: store.history.future.length > 0,
    record,
    undo,
    redo,
    clear
  };
};
